import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import TaskEdit from 'components/Task/TaskEdit';
import { FormRef } from '@lib/components/ReactHookForm/types';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import useSearchListQueries from 'routes/Task/utils/useSearchListQueries';
import getTaskFields from 'routes/Task/utils/useFields/getTaskFields';
import useModalManagement from '@lib/hooks/useModalManagement';
import DocumentsModalTableMultiselect from 'components/DocumentsModalTableMultiselect/DocumentsModalTableMultiselect';
import DepartmentsModalTableMultiselect from 'components/DepartmentsModalTableMultiselect/DepartmentsModalTableMultiselect';
import { Option } from '@lib/interfaces/form';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  FilterGroupingEnum,
} from 'graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import _get from 'lodash/get';
import { FieldNames } from './enums';
import useActions from './utils/useActions';
import useDocumentationLinksPrefill from './utils/useDocumentationLinksPrefill';
import useDepartmentLinksPrefill from './utils/useDepartmentLinksPrefill';

const SELECT_DOCUMENTS_MODAL = 'select-documents';
const SELECT_DEPARTMENTS_MODAL = 'select-departments';

export default function TaskEditContainer() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const formRef = useRef<FormRef>(undefined);

  const { getUserAccessByComponent, getUserAccessByCompany }: ContextProps =
    useOutletContext();
  const isDocumentationLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead ||
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead;

  const {
    getApproversForEditFormLoadOptions: getApproversLoadOptions,
    getAssetsLoadOptions,
    getDocumentationsLoadOptions,
    getDepartmentsLoadOptions,
    getSiteAreasLoadOptions,
    getSitesLoadOptions,
    getTemplateVersionsLoadOptions,
    getTemplatesLoadOptions,
  } = useSearchListQueries();

  // Documents modal
  const useModalManagementResult = useModalManagement([
    SELECT_DOCUMENTS_MODAL,
    SELECT_DEPARTMENTS_MODAL,
  ]);

  const openDocumentsModalTableMultiselect = useCallback(() => {
    useModalManagementResult.openModal(SELECT_DOCUMENTS_MODAL);
  }, [useModalManagementResult]);

  const onCloseDocumentsModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_DOCUMENTS_MODAL);
  }, [useModalManagementResult]);

  const onDocumentsSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        FieldNames.documentationLinks,
        values.length ? values.map((docId) => ({ value: docId })) : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const isDocumentsModalOpen = useModalManagementResult.isModalOpened(
    SELECT_DOCUMENTS_MODAL,
  );

  // Departments modal
  const openDepartmentsModalTableMultiselect = useCallback(() => {
    useModalManagementResult.openModal(SELECT_DEPARTMENTS_MODAL);
  }, [useModalManagementResult]);

  const onCloseDepartmentsModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_DEPARTMENTS_MODAL);
  }, [useModalManagementResult]);

  const onDepartmentsSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        FieldNames.departmentLinks,
        values.length
          ? values.map((departmentId) => ({ value: departmentId }))
          : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const isDepartmentsModalOpen = useModalManagementResult.isModalOpened(
    SELECT_DEPARTMENTS_MODAL,
  );

  const initialDocumentationLinksRef = useRef<Option[]>([]);
  const initialDepartmentLinksRef = useRef<Option[]>([]);

  const {
    onUpdate,
    onViewFormTemplate,
    taskData,
    taskError,
    taskLoading,
    updateMutationError,
    updateTaskLoading,
  } = useActions({
    formRef,
    initialDocumentationLinksRef,
    initialDepartmentLinksRef,
    isDocumentationLocked,
  });

  const { loading: documentationLinksPrefillLoading } =
    useDocumentationLinksPrefill({
      formRef,
      groupKey: taskData?.groupKey,
      initialDocumentationLinksRef,
      skip: isDocumentationLocked,
    });

  const { loading: departmentLinksPrefillLoading } = useDepartmentLinksPrefill({
    formRef,
    groupKey: taskData?.groupKey,
    initialDepartmentLinksRef,
  });

  const canFormTemplateUpdate =
    _get(taskData, 'formTemplate.canUpdate.value') || false;
  const canQrCodeRelatedInfoUpdate =
    _get(taskData, 'canQrCodeRelatedInfoUpdate.value') || false;
  const formFields = getTaskFields({
    getApproversLoadOptions,
    getAssetsLoadOptions,
    getDocumentationsLoadOptions,
    getDepartmentsLoadOptions,
    getSiteAreasLoadOptions,
    getSitesLoadOptions,
    canQrCodeRelatedInfoUpdate,
    getTemplateVersionsLoadOptions: canFormTemplateUpdate
      ? getTemplateVersionsLoadOptions
      : undefined,
    getTemplatesLoadOptions: canFormTemplateUpdate
      ? getTemplatesLoadOptions
      : undefined,
    isDocumentationLocked,
    onViewFormTemplate,
    openDocumentsModalTableMultiselect,
    openDepartmentsModalTableMultiselect,
    t,
    isCreate: false,
  });

  const breadCrumbsData = getBreadCrumbsDataByKey({
    pathname,
    t,
    entity: { name: taskData?.name, id: taskData?.id },
  });

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      <TaskEdit
        updateMutationError={updateMutationError}
        updateTaskLoading={updateTaskLoading}
        formFields={formFields}
        formRef={formRef}
        onSubmit={onUpdate}
        taskData={taskData}
        taskError={taskError}
        taskLoading={
          taskLoading ||
          documentationLinksPrefillLoading ||
          departmentLinksPrefillLoading
        }
      />
      <DocumentsModalTableMultiselect
        isOpen={isDocumentsModalOpen}
        onClose={onCloseDocumentsModal}
        onSelect={onDocumentsSelect}
        formRef={formRef}
        fieldName={FieldNames.documentationLinks}
      />
      <DepartmentsModalTableMultiselect
        isOpen={isDepartmentsModalOpen}
        onClose={onCloseDepartmentsModal}
        onSelect={onDepartmentsSelect}
        formRef={formRef}
        fieldName={FieldNames.departmentLinks}
        selectedTabFilters={
          taskData?.groupKey
            ? [
                {
                  departmentTaskGroupsGroupKey: {
                    grouping: FilterGroupingEnum.And,
                    predicate: { eq: taskData.groupKey },
                  },
                },
              ]
            : undefined
        }
      />
    </>
  );
}
